import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useUser } from "../context/userContext";
import api from "../utils/api";
import { extractErrorMessage } from "../utils/apiError";
import { API_ENDPOINTS } from "../config/api";
import { setPageTitle } from "../utils/pageTitle";
import { toast } from "../utils/toast";
import { filenameForUpload, isUploadCancelledError } from "../utils/upload";
import { useDirectUpload } from "../hooks/use-direct-upload";
import { ActionTiles } from "../components/action-tiles";
import { UploadDialog } from "../components/upload-dialog";
import { RecordDialog } from "../components/record-dialog";
import {
  SUPPORTED_LANGS,
  DEFAULT_LANG,
  detectTextLang,
  normalizeLang,
  translations,
} from "../utils/i18n";

const PERSONA_OPTIONS = [
  { value: "personal_use", Icon: SparkleIcon },
  { value: "creator", Icon: CreatorIcon },
  { value: "content_business", Icon: MicIcon },
  { value: "voice_actor", Icon: WaveformIcon },
  { value: "engineer", Icon: CodeIcon },
  { value: "marketer", Icon: StorefrontIcon },
  { value: "education", Icon: GraduationCapIcon },
  { value: "other", Icon: OtherIcon },
];

const CAPTION_FORMATS = ["MP4", "MOV", "MKV", "WEBM"];
// The onboarding upload always lands in this same project, so a first-time
// user has somewhere to see it land on the dashboard instead of showing up
// unfiled.
const FIRST_PROJECT_NAME = "Testing The Water";

export default function OnboardingPage() {
  const { completeOnboarding, user, setHoldOnboardingRedirect } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang: langParam } = useParams();
  const language = normalizeLang(langParam);
  const tr = translations[language].onboarding;
  const trUpload = translations[language].uploadFlow;
  const [step, setStep] = useState(1);
  // Google sign-up/login never puts a name into the backend record (see
  // GoogleAuthSerializer), so both Google auth paths — the FedCM button and
  // the mobile-Safari redirect fallback (see GoogleSignInButton) — hand the
  // name from the Google account along via navigation state for this
  // one-time prefill.
  const [fullName, setFullName] = useState(() => user?.full_name || location.state?.googleFullName || "");
  const [persona, setPersona] = useState("");
  const [claimStatus, setClaimStatus] = useState("idle"); // idle | submitting | claimed
  const [organization, setOrganization] = useState(() => user?.organization ?? null);
  const [activeDialog, setActiveDialog] = useState(null); // null | "upload" | "record" | "caption"
  const isOrganizationMember = Boolean(user?.organization);
  // The claim-credit step only exists for org members, so it (and therefore
  // the final "start a project" step after it) shifts by one depending on
  // whether it's in the flow at all.
  const totalSteps = isOrganizationMember ? 4 : 3;
  const claimCreditStep = isOrganizationMember ? 3 : null;
  const submitting = claimStatus === "submitting";
  const redirectTimer = useRef(null);
  const firstProjectUpload = useDirectUpload({ scope: "home" });

  useEffect(() => {
    setPageTitle("Onboarding");
    return () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
      setHoldOnboardingRedirect(false);
    };
  }, [setHoldOnboardingRedirect]);

  // Keeps the URL a valid /onboarding/en or /onboarding/ar even if someone
  // lands on an unsupported segment (e.g. /onboarding/fr).
  useEffect(() => {
    if (!SUPPORTED_LANGS.includes(langParam)) {
      navigate(`/onboarding/${DEFAULT_LANG}`, { replace: true });
    }
  }, [langParam, navigate]);

  useEffect(() => {
    const organizationId = user?.organization?.id;
    if (!organizationId) return undefined;

    let active = true;
    api.get(API_ENDPOINTS.ORGANIZATION.DETAIL(organizationId))
      .then(({ data }) => {
        if (active) setOrganization(data);
      })
      .catch(() => {
        // GET /user/me may already contain the fields needed by this step.
      });

    return () => {
      active = false;
    };
  }, [user?.organization?.id]);

  function handleNameSubmit(e) {
    e.preventDefault();
    setStep(2);
  }

  async function finishOnboarding(personaValue) {
    setClaimStatus("submitting");
    // completeOnboarding() flips the user to "onboarded" in context the
    // moment it resolves, which OnboardingFlowRoute otherwise treats as a
    // signal to redirect away immediately. Hold that off — beyond the
    // claim-credit animation below, the final "start a project" step still
    // needs to render — and only clear it once that step sends the user on
    // to the dashboard itself (see goToDashboard).
    setHoldOnboardingRedirect(true);
    // The API call is often near-instant, which would make the "submitting"
    // state flash by unnoticed — hold it open a bit so it reads as a step.
    const minSubmittingDelay = new Promise((resolve) => setTimeout(resolve, 900));
    try {
      await Promise.all([
        completeOnboarding(fullName, personaValue, language),
        minSubmittingDelay,
      ]);
      if (isOrganizationMember) {
        setClaimStatus("claimed");
        redirectTimer.current = setTimeout(() => {
          setClaimStatus("idle");
          setStep(totalSteps);
        }, 3200);
      } else {
        setClaimStatus("idle");
        setStep(totalSteps);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setClaimStatus("idle");
      setHoldOnboardingRedirect(false);
    }
  }

  function handlePersonaSelect(value) {
    setPersona(value);
    if (isOrganizationMember) {
      setStep(3);
      return;
    }
    finishOnboarding(value);
  }

  function handlePersonaSkip() {
    setPersona("");
    if (isOrganizationMember) {
      setStep(3);
      return;
    }
    finishOnboarding("");
  }

  function goToDashboard() {
    setHoldOnboardingRedirect(false);
    navigate("/transcribe");
  }

  async function handleFirstProjectUpload(fileOrBlob, source, customName) {
    try {
      const { data: project } = await api.post(API_ENDPOINTS.PROJECT.LIST, { name: FIRST_PROJECT_NAME });
      const filename = filenameForUpload(fileOrBlob, source === "recorded" ? customName : undefined);
      const name = customName || filename;
      const meta = { filename, language: "ar", source, name, project: project?.id ?? null };
      await firstProjectUpload.start(fileOrBlob, meta);
      toast.success(trUpload.uploadComplete);
      goToDashboard();
    } catch (err) {
      if (!isUploadCancelledError(err)) toast.error(extractErrorMessage(err));
      throw err;
    }
  }

  const companyName = organization?.name ?? user?.organization?.name ?? "";
  const baseCredit = organization?.base_credit ?? user?.organization?.base_credit;
  const formattedBaseCredit = baseCredit == null
    ? ""
    : new Intl.NumberFormat(language).format(baseCredit);

  return (
    <div
      lang={language}
      dir={language === "ar" ? "rtl" : "ltr"}
      className={`onboarding-page ${step === 1 ? "onboarding-page-narrow" : "onboarding-page-wide"}`}
    >
      <div className="auth-content">
        {step === 1 ? (
          <div className="auth-card">
            <form onSubmit={handleNameSubmit}>
              <h1>{tr.step1.title}</h1>
              <div className="field">
                <label htmlFor="fullName">{tr.step1.nameLabel}</label>
                <input
                  id="fullName"
                  type="text"
                  placeholder={tr.step1.namePlaceholder}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  autoFocus
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="language-en">{tr.step1.preferLabel}</label>
                <div className="segmented" dir="ltr" role="radiogroup" aria-label="Preferred language">
                  <button
                    id="language-en"
                    type="button"
                    role="radio"
                    aria-checked={language === "en"}
                    className={`segmented-option${language === "en" ? " is-active" : ""}`}
                    onClick={() => navigate("/onboarding/en", { replace: true })}
                  >
                    English
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={language === "ar"}
                    className={`segmented-option${language === "ar" ? " is-active" : ""}`}
                    onClick={() => navigate("/onboarding/ar", { replace: true })}
                    lang="ar"
                  >
                    عربي
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className="btn-full"
                disabled={!fullName.trim()}
              >
                {tr.step1.next}
              </button>
            </form>
          </div>
        ) : step === 2 ? (
          <div className="persona-card">
            <h1>{tr.step2.title}</h1>
            <div className="persona-grid">
              {PERSONA_OPTIONS.map(({ value, Icon }) => (
                <button
                  key={value}
                  type="button"
                  className={"persona-option" + (persona === value ? " is-active" : "")}
                  onClick={() => handlePersonaSelect(value)}
                  disabled={submitting}
                >
                  <Icon />
                  <span>{tr.step2.personas[value]}</span>
                </button>
              ))}
            </div>
            <div className="onboarding-links">
              <button
                type="button"
                className="btn-plain"
                onClick={() => setStep(1)}
                disabled={submitting}
              >
                {tr.step2.back}
              </button>
              <button
                type="button"
                className="btn-plain"
                onClick={handlePersonaSkip}
                disabled={submitting}
              >
                {tr.step2.skip}
              </button>
            </div>
          </div>
        ) : step === claimCreditStep ? (
          claimStatus === "claimed" ? (
            <div className="claim-credit-view claim-credit-success" key="claimed">
              <div className="claim-success-check" aria-hidden="true">
                <svg viewBox="0 0 52 52">
                  <circle className="claim-success-check-circle" cx="26" cy="26" r="24" />
                  <path className="claim-success-check-mark" fill="none" d="M14 27l7 7 17-17" />
                </svg>
              </div>
              <h1>{tr.claimCredit.claimedTitle}</h1>
              <p>{tr.claimCredit.claimedBody(tr.claimCredit.creditAmount(formattedBaseCredit))}</p>
            </div>
          ) : (
            <div className="claim-credit-view" key="claim">
              <h1>
                {tr.claimCredit.titlePrefix}
                <span lang={detectTextLang(companyName)}>{companyName}</span>
                {tr.claimCredit.titleSuffix}
              </h1>
              <p>
                {tr.claimCredit.grantPrefix}
                <span lang={detectTextLang(companyName)}>{companyName}</span>
                {tr.claimCredit.grantAfterCompany}
                <strong>{tr.claimCredit.creditAmount(formattedBaseCredit)}</strong>
                {tr.claimCredit.grantSuffix}
              </p>
              <div className="claim-credit-actions">
                <button
                  type="button"
                  className="btn-full"
                  onClick={() => finishOnboarding(persona)}
                  disabled={submitting}
                >
                  <span className="claim-btn-label" key={submitting ? "claiming" : "claim"}>
                    {submitting && <Loader2 className="claim-btn-spinner" aria-hidden="true" />}
                    {submitting ? tr.claimCredit.claiming : tr.claimCredit.claim}
                  </span>
                </button>
              </div>
            </div>
          )
        ) : (
          <div className="persona-card first-project-card">
            <h1>{tr.startProject.title}</h1>
            <p className="subtitle">{tr.startProject.subtitle}</p>
            <ActionTiles
              onUpload={() => setActiveDialog("upload")}
              onRecord={() => setActiveDialog("record")}
              onCaption={() => setActiveDialog("caption")}
              uploading={firstProjectUpload.busy}
            />
            <div className="onboarding-links">
              <button
                type="button"
                className="btn-plain"
                onClick={goToDashboard}
                disabled={firstProjectUpload.busy}
              >
                {tr.startProject.skip}
              </button>
            </div>
          </div>
        )}
        <div className="step-dots" aria-hidden="true">
          {Array.from({ length: totalSteps }, (_, i) => (
            <span key={i} className={i + 1 === step ? "is-active" : undefined} />
          ))}
        </div>
      </div>
      <UploadDialog
        open={activeDialog === "upload"}
        onOpenChange={(open) => setActiveDialog(open ? "upload" : null)}
        onUpload={handleFirstProjectUpload}
        uploading={firstProjectUpload.busy}
        upload={firstProjectUpload}
      />
      <RecordDialog
        open={activeDialog === "record"}
        onOpenChange={(open) => setActiveDialog(open ? "record" : null)}
        onUpload={handleFirstProjectUpload}
        uploading={firstProjectUpload.busy}
        upload={firstProjectUpload}
      />
      <UploadDialog
        open={activeDialog === "caption"}
        onOpenChange={(open) => setActiveDialog(open ? "caption" : null)}
        onUpload={handleFirstProjectUpload}
        uploading={firstProjectUpload.busy}
        upload={firstProjectUpload}
        source="caption"
        title={trUpload.captionUploadTitle}
        prompt={trUpload.captionUploadPrompt}
        accept="video/*"
        formats={CAPTION_FORMATS}
      />
    </div>
  );
}

function SparkleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2c.3 3.6 1 6.7 2.1 7.9C15.3 11 18.4 11.7 22 12c-3.6.3-6.7 1-7.9 2.1C12.9 15.3 12.2 18.4 12 22c-.3-3.6-1-6.7-2.1-7.9C8.7 12.9 5.6 12.2 2 12c3.6-.3 6.7-1 7.9-2.1C11 8.7 11.7 5.6 12 2z" />
    </svg>
  );
}

function CreatorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <circle cx="9.5" cy="9.5" r="1.5" />
      <path d="M4 16l4.5-4.5 3 3L18 8.5l2 2" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

function WaveformIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="10" x2="3" y2="14" />
      <line x1="7" y1="6" x2="7" y2="18" />
      <line x1="11" y1="3" x2="11" y2="21" />
      <line x1="15" y1="7" x2="15" y2="17" />
      <line x1="19" y1="10" x2="19" y2="14" />
    </svg>
  );
}

function CodeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="8 5 3 12 8 19" />
      <polyline points="16 5 21 12 16 19" />
    </svg>
  );
}

function StorefrontIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 9l1-5h14l1 5" />
      <path d="M4 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" />
      <path d="M5 9v10h14V9" />
      <path d="M10 19v-6h4v6" />
    </svg>
  );
}

function GraduationCapIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10L12 5 2 10l10 5 10-5z" />
      <path d="M6 12v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" />
    </svg>
  );
}

function OtherIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </svg>
  );
}
